import {
  DEFAULT_COMPARE_DURATION_A_NAME,
  DEFAULT_COMPARE_DURATION_B_NAME,
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import { useGetCompareColors } from '@modules/quantify/hooks/useGetCompareColors';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { CompareReportDataPoint } from '@modules/quantify/store/types';
import { getCompareLabelPrefix } from '@modules/quantify/utils';
import { CircleRounded } from '@mui/icons-material';
import { Box, Link, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface CompareChartTooltipRecordsCountProps {
  payload: CompareReportDataPoint | null;
  onViewFeedback: ({ isFieldA }: { isFieldA: boolean }) => void;
}

export const CompareChartTooltipRecordsCount = ({
  payload,
  onViewFeedback,
}: CompareChartTooltipRecordsCountProps) => {
  const { colorA, colorB } = useGetCompareColors();
  const compareTo = useQuantifyStore(({ query }) => query.compareTo);
  const formulaID = useQuantifyStore(({ query }) => query.formulaID);
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.measuredAsCriteria,
  );
  const compareFilterAName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareQBField.compareQBFieldAName ||
      DEFAULT_COMPARE_FILTER_A_NAME,
  );
  const compareFilterBName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareQBField.compareQBFieldBName ||
      DEFAULT_COMPARE_FILTER_B_NAME,
  );
  const compareDurationAName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareDurationField.compareDurationAName ||
      DEFAULT_COMPARE_DURATION_A_NAME,
  );
  const compareDurationBName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareDurationField.compareDurationBName ||
      DEFAULT_COMPARE_DURATION_B_NAME,
  );

  const compareLabelPrefix = getCompareLabelPrefix({
    formulaID,
    measuredAsCriteria,
  });

  const renderFieldRecordCount = useCallback(
    ({ isFieldA }: { isFieldA: boolean }) => {
      const color = isFieldA ? colorA : colorB;
      const value = isFieldA ? payload?.currValue : payload?.prevValue;
      const compareFilterLabel = isFieldA
        ? compareFilterAName
        : compareFilterBName;
      const compareDurationLabel = isFieldA
        ? compareDurationAName
        : compareDurationBName;
      const compareFieldLabel =
        compareTo?.type !== 'whereClause'
          ? compareDurationLabel
          : compareFilterLabel;
      const criteriaDisplayName = measuredAsCriteria
        ? measuredAsCriteria.displayName
        : 'feedback records';

      if (formulaID === EQLFunctionName.NormaliseByPercent) {
        return (
          <Stack direction="row" spacing={0.7}>
            <CircleRounded sx={{ width: 14, mt: -0.1 }} style={{ color }} />
            <Typography variant="body2">
              {value ? (
                <Link
                  component="button"
                  onClick={() => onViewFeedback({ isFieldA })}
                  color="primary"
                >
                  {value}%
                </Link>
              ) : (
                <Box color="text.secondary" component="span">
                  0%
                </Box>
              )}{' '}
              of total {criteriaDisplayName.toLowerCase()} matching{' '}
              {compareFieldLabel}
            </Typography>
          </Stack>
        );
      }

      return (
        <Stack direction="row" spacing={0.5}>
          <CircleRounded sx={{ width: 14 }} style={{ color }} />
          <Stack spacing={1} direction="row" alignItems="baseline">
            <Typography variant="body2">
              {compareLabelPrefix} {compareFieldLabel}:
            </Typography>
            {value ? (
              <Link
                component="button"
                variant="body1"
                onClick={() => onViewFeedback({ isFieldA })}
                color="primary"
              >
                {value}
              </Link>
            ) : (
              <Box color="text.secondary" component="span">
                0
              </Box>
            )}
          </Stack>
        </Stack>
      );
    },
    [
      colorA,
      colorB,
      payload?.currValue,
      payload?.prevValue,
      compareFilterAName,
      compareFilterBName,
      compareDurationAName,
      compareDurationBName,
      compareTo?.type,
      measuredAsCriteria,
      formulaID,
      compareLabelPrefix,
      onViewFeedback,
    ],
  );

  return (
    <Stack spacing={1} px={1.5}>
      {formulaID === EQLFunctionName.NormaliseByPercent && (
        <Typography variant="body2">Present on: </Typography>
      )}
      {renderFieldRecordCount({ isFieldA: true })}
      {renderFieldRecordCount({ isFieldA: false })}
    </Stack>
  );
};
